/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

/** @jsx jsx */
import { jsx } from "@emotion/core"
import { useState } from "react"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { actionCreator } from "store/duck/menu"
import { Form } from "grab-ui"

import { CustomSelect } from "components/common/CustomSelect"
import { AttributeCluster } from "types/models/menu"
import { DEFAULT_PAGE_SIZE_ATTRIBUTE_OPTION } from "constants/menu"
import { ItemAttributeValue } from "types/models/item_attribute"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { getGrabXValueFromLocalStorage, MerchantPortalFeatureFlag } from "grabx"
import { useLocaleContext } from "common/utils/i18n"
import styles from "./AttributeDrawer.modules.css"
import { notShowToPax } from "./AttributeDrawer.utils"

const MIN_SEARCH_CHARS = 3
const SEARCH_DEBOUNCE_MS = 500

export interface AttributeDrawerPreDefinedProps extends FormComponentProps {
  attributeCluster: AttributeCluster
  isLoadingPagination?: boolean
  selectedAttributes?: ItemAttributeValue[]
  initialValueAttributes?: ItemAttributeValue[]
  handleChangeAttributes: (value?: ItemAttributeValue[]) => void
  itemID?: string
  disableFormRule?: boolean
}

export function AttributeDrawerPreDefined({
  form,
  attributeCluster,
  isLoadingPagination,
  itemID,
  selectedAttributes,
  handleChangeAttributes,
  initialValueAttributes,
  disableFormRule,
}: AttributeDrawerPreDefinedProps) {
  const textMap = useTranslationsMapTyped({
    warningLimitExceed: "itemAttributes.error.limitExceed",
    warningRequired: "itemAttributes.error.required",
    infoNotShownToPax: "itemAttributes.info.notShownToPax",
    searchPlaceholder:
      "ecommerce.sellerCentre.products.categoryPicker.searchPlaceholder",
  })

  const [page, setPage] = useState(1)
  const [filter, setFilter] = useState<string>()
  const attributeLocalizationEnabled = getGrabXValueFromLocalStorage<boolean>(
    MerchantPortalFeatureFlag.attributeLocalizationEnabled,
  )

  const locale = useLocaleContext("locale")
  const nameLabel = attributeLocalizationEnabled
    ? attributeCluster.nameLocalization
    : attributeCluster.name
  const {
    merchantID,
    merchantGroupID,
    isMenuGroup,
    menuGroupDetail: { menuGroupID },
  } = useSelector((state: AppState) => state?.menu, shallowEqual)
  const dispatch = useDispatch()
  const optionsLimit =
    attributeCluster.metadata?.preDefinedMetadata?.attributesLimitPerItem

  const { getFieldDecorator } = form

  const attributeClusterOption = attributeCluster?.attributeClusterOptions?.map(
    value => ({
      label: attributeLocalizationEnabled ? value.nameLocalization : value.name,
      value: attributeLocalizationEnabled ? value.nameLocalization : value.name,
    }),
  )
  const total =
    attributeCluster.attributeClusterOptionsHasMore &&
    attributeCluster.attributeClusterOptions
      ? page * attributeCluster.attributeClusterOptions.length +
        DEFAULT_PAGE_SIZE_ATTRIBUTE_OPTION
      : page * DEFAULT_PAGE_SIZE_ATTRIBUTE_OPTION

  const handleSearchOption = (value: string) => {
    setPage(1)
    setFilter(value)
    dispatch(
      actionCreator.fetchAttributeClusterOption(
        merchantID,
        merchantGroupID,
        isMenuGroup,
        menuGroupID,
        {
          attributeClusterIDs: attributeCluster.id,
          page: 1,
          name: value,
          locale,
        },
      ),
    )
  }

  const handleSelectOption = (value: string) => {
    const selectedOptionData = attributeCluster.attributeClusterOptions?.find(
      key =>
        attributeLocalizationEnabled
          ? key.nameLocalization === value
          : key.name === value,
    )
    const attributeSelected: ItemAttributeValue = {
      attributeID: selectedOptionData?.id,
      cluster: selectedOptionData?.cluster,
      itemID,
      status: selectedOptionData?.status || "Enabled",
      value,
      attributeClusterID: selectedOptionData?.attributeClusterID,
    }
    let selectedAttributeCustom = selectedAttributes
    if (optionsLimit === 1) {
      selectedAttributeCustom = selectedAttributeCustom?.filter(
        key => key.cluster !== selectedOptionData?.cluster,
      )
    }
    const finalValue = selectedAttributeCustom
      ? selectedAttributeCustom.concat(attributeSelected)
      : [attributeSelected]
    handleChangeAttributes(finalValue)
  }

  const handleDeselectOption = (value: string) => {
    const finalValue = selectedAttributes?.filter(key => key.value !== value)
    handleChangeAttributes(finalValue)
  }

  const handleRemoveChange = (value: string) => {
    if (value === undefined) {
      const finalValue = selectedAttributes?.filter(
        key => key.attributeClusterID !== attributeCluster.id,
      )
      handleChangeAttributes(finalValue)
    }
  }

  const handleSearchOptionPaginationChange = (value: number) => {
    setPage(value)
    dispatch(
      actionCreator.fetchAttributeClusterOption(
        merchantID,
        merchantGroupID,
        isMenuGroup,
        menuGroupID,
        {
          attributeClusterIDs: attributeCluster.id,
          page: value,
          name: filter,
          locale,
        },
      ),
    )
  }

  const limitCheckValidator = (
    rule: any,
    value: string | string[],
    callback: any,
  ) => {
    if (
      Array.isArray(value) &&
      optionsLimit &&
      value?.length > optionsLimit &&
      optionsLimit > 1
    ) {
      callback(rule)
    } else {
      callback()
    }
  }

  const initialValues = initialValueAttributes?.filter(
    (key: ItemAttributeValue) => key.attributeClusterID === attributeCluster.id,
  )

  const initialValue = initialValues?.map(initial => initial.value)

  const finalInitialValue =
    initialValue?.length === 1 ? initialValue[0] : initialValue

  return (
    <div
      className={styles.formWrapper}
      data-testid="attribute-drawer-predefined"
    >
      <Form.Item label={nameLabel}>
        {getFieldDecorator(`attributeForm.${nameLabel}`, {
          initialValue: finalInitialValue,
          rules: disableFormRule
            ? undefined
            : [
                {
                  required:
                    attributeCluster?.attributeClusterOption?.isMandatory,
                  message: textMap.warningRequired,
                },
                {
                  validator: limitCheckValidator,
                  message: textMap.warningLimitExceed,
                },
              ],
        })(
          <CustomSelect
            mode={optionsLimit && optionsLimit > 1 ? "multiple" : undefined}
            placeholder="Please Choose"
            data-testid="input-attribute"
            searchable
            allowClear={optionsLimit === 1}
            options={attributeClusterOption}
            loading={isLoadingPagination}
            filterOption={false}
            minSearchChars={MIN_SEARCH_CHARS}
            searchProps={{
              placeholder: textMap.searchPlaceholder.replace(
                "<number>",
                String(MIN_SEARCH_CHARS),
              ),
            }}
            searchDelayMs={SEARCH_DEBOUNCE_MS}
            onSearch={handleSearchOption}
            onSelect={handleSelectOption}
            onDeselect={handleDeselectOption}
            onChange={handleRemoveChange}
            paginationProps={{
              current: page,
              showPagination: attributeCluster.attributeClusterOptionsHasMore,
              onChange: handleSearchOptionPaginationChange,
              pageSize: DEFAULT_PAGE_SIZE_ATTRIBUTE_OPTION,
              total,
              hideOnSinglePage: true,
            }}
          />,
        )}
      </Form.Item>
      {!attributeCluster.attributeClusterOption.isShownToPax &&
        notShowToPax(textMap.infoNotShownToPax)}
    </div>
  )
}
